import React, { useCallback } from 'react'
import { EditorState } from 'draft-js'
import { Input, Radio } from 'antd'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../store/store'
import { decorator, translateVariable } from '../RichEditor/utils'
import {
  DEFAULT_VARIABLE_LINE_STYLE,
  VARIABLE_LINE_STYLES,
  findVariableEntityRange,
  replaceVariableOccurrence,
} from '../../utils/variableLineStyle'
import { variableSupportsLineStyle } from './variablesUtils'
import { documentEditorKey, getActiveField } from '../../utils/activeFields'

// Nombre de lignes d'une occurrence de variable multi-lignes (adresse : rue,
// complément, code postal + ville). Le choix est stocké dans la data de
// l'entité Draft, donc dans le contenu du document : réglé sur un modèle, il
// suit les propositions qui en sont issues. Deux insertions de la même variable
// se règlent indépendamment (en-tête sur une ligne, bloc destinataire sur trois).
const VariableLineStyleControl = ({ framed = false }) => {
  const { t } = useTranslation()
  const occurrence = useStore(({ variableOccurrenceSelected }) => variableOccurrenceSelected)
  const boxVariableSelectedId = useStore(({ boxVariableSelectedId }) => boxVariableSelectedId)
  const updateBox = useStore(({ updateBox }) => updateBox)
  const isTemplate = useStore(({ currentDocument }) => currentDocument.type === 'template')
  // Une variable peut vivre dans un masque (en-tête / pied de page) : la box
  // n'est alors pas dans `boxes`.
  const box = useStore(({ boxes, masks }) =>
    occurrence
      ? [...boxes, ...masks.map((m) => m.boxes).flat()].find((b) => b.id === occurrence.boxId)
      : null
  )
  const variableValue = useStore(({ variables }) => variables[occurrence?.variableKey]?.value)

  // Box en cours d'édition : le store n'a son contenu qu'à la désélection (une
  // variable tout juste insérée n'y est pas encore), seul l'éditeur fait foi.
  // Pas de mémo : l'état de l'éditeur change sans passer par le store. Le
  // clic sur la variable et chaque réglage (qui écrit aussi le store)
  // relancent le rendu.
  const editor = occurrence ? getActiveField(documentEditorKey(occurrence.boxId)) : null
  const target = findVariableEntityRange(
    editor ? editor.getEditorState() : box?.content?.editorState,
    occurrence?.entityKey
  )

  // `change` ne porte que le réglage modifié (style ou séparateur) : l'autre
  // est relu sur l'entité.
  const handleChange = useCallback((change) => {
    if (!occurrence) return
    // Lecture à la volée : évite d'abonner le panneau à tout l'objet variables.
    const { variables } = useStore.getState()

    // Relu au moment d'écrire : le contenu a pu bouger depuis le rendu.
    const rewrite = (editorState) => {
      const current = findVariableEntityRange(editorState, occurrence.entityKey)
      if (!current) return null

      const { lineStyle, separator } = { ...current.data, ...change }
      const text = translateVariable({
        variableKey: current.data.key,
        variables,
        isTemplate,
        lineStyle,
        separator,
      })
      return replaceVariableOccurrence({
        editorState,
        entityKey: occurrence.entityKey,
        text,
        lineStyle,
        separator,
      })
    }

    // Box en cours d'édition (mode proposition : le clic sur la variable la
    // sélectionne) : seul son éditeur fait foi, cf. RichEditor.
    const editor = getActiveField(documentEditorKey(occurrence.boxId))
    if (editor) {
      editor.replaceContent(rewrite)
      return
    }

    updateBox(occurrence.boxId, (b) => {
      const content = rewrite(b.content?.editorState)
      if (!content) return

      b.content.editorState = EditorState.createWithContent(content, decorator)
    })
  }, [occurrence, updateBox, isTemplate])

  if (!occurrence || !target) return null
  // L'occurrence est périmée dès qu'une autre variable prend la sélection :
  // handleBoxVariableSelectedId la remet à null, mais un clic refusé (verrou
  // d'un co-éditeur) peut laisser les deux désynchronisés.
  if (boxVariableSelectedId !== occurrence.variableKey) return null
  if (!variableSupportsLineStyle(occurrence.variableKey, variableValue)) return null

  const lineStyle = target.data.lineStyle ?? DEFAULT_VARIABLE_LINE_STYLE

  const options = [
    { label: t('variable-line-style-full'), value: VARIABLE_LINE_STYLES.FULL },
    { label: t('variable-line-style-two'), value: VARIABLE_LINE_STYLES.TWO },
    { label: t('variable-line-style-one'), value: VARIABLE_LINE_STYLES.ONE },
  ]

  // Hors panneau d'édition rapide (mode proposition), le réglage est seul dans
  // le panel droit : il porte son propre cadre.
  const containerStyle = framed
    ? {
        border: '1px solid rgba(11, 34, 57, 0.3)',
        borderRadius: 8,
        padding: '8px 12px 12px',
        margin: '4px 16px 16px',
      }
    : { marginTop: 8 }

  return (
    <div style={containerStyle}>
      <div style={{ marginBottom: 4 }}>{t('variable-line-style')}</div>
      <Radio.Group
        value={lineStyle}
        onChange={(e) => handleChange({ lineStyle: e.target.value })}
        style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
      >
        {options.map(({ label, value }) => (
          <Radio key={value} value={value} style={{ fontSize: 12 }}>
            {label}
          </Radio>
        ))}
      </Radio.Group>
      {/* En « Complet » chaque ligne garde son retour à la ligne : aucun
          séparateur à choisir. */}
      {lineStyle !== VARIABLE_LINE_STYLES.FULL && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ fontSize: 12 }}>{t('variable-line-separator')}</span>
          <Input
            size='small'
            maxLength={3}
            value={target.data.separator ?? ''}
            onChange={(e) => handleChange({ separator: e.target.value })}
            style={{ width: 56 }}
          />
        </div>
      )}
      {/* Sans complément d'adresse, « Complet » rend la même chose que
          « Sur 2 lignes » : la différence vient de la fiche, pas du réglage. */}
      <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(11, 34, 57, 0.45)' }}>
        {t('variable-line-style-hint')}
      </div>
    </div>
  )
}

export default VariableLineStyleControl
